import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_ONBOARDING_APP_URL } from "@redotech/redo-merchant-app-common/config";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  FIRST_INSTALL_ONBOARDING_VERSION,
  shouldAskForFirstInstallProduct,
  type FirstInstallAttributionSource,
  type FirstInstallOnboardingDestination,
  type FirstInstallOnboardingProduct,
} from "@redotech/redo-model/onboarding";
import type { InstallSource } from "@redotech/redo-model/shopify";
import { RedoElevatedOnboarding } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding";
import { RedoElevatedOnboardingHeroPanel } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding-hero-panel";
import { Text } from "@redotech/redo-web/text";
import {
  openDeferredWindow,
  type DeferredWindow,
} from "@redotech/web-util/window";
import classNames from "classnames";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  completeFirstInstallOnboarding,
  getCurrentOnboarding,
  onboardingTc,
} from "../client/onboarding";
import { AnimatedLogos } from "../setup-guide/components/intake-modal/animated-logos";
import { DecryptedText } from "../setup-guide/components/intake-modal/decrypted-text";
import * as modalCss from "../setup-guide/components/intake-modal/intake-modal.module.css";
import { IntakeOption } from "../setup-guide/components/intake-modal/intake-option";
import { IntakeStepTransition } from "../setup-guide/components/intake-modal/step-transition";
import { RECLAIM_ATTRIBUTION_QUESTION } from "../setup-guide/products/disputes/attribution";
import { useSetupGuideOptional } from "../setup-guide/provider/use-setup-guide";
import {
  getFirstInstallContinueText,
  getInitialFirstInstallStep,
  getNextFirstInstallAction,
  startFirstInstallNativeSetupGuideIfAvailable,
  toFirstInstallAttributionSource,
  type FirstInstallStep,
} from "./first-install-flow";
import {
  FIRST_INSTALL_BACK_PRODUCT_OPTION,
  FIRST_INSTALL_OTHER_PRODUCT_CHOICES,
  FIRST_INSTALL_OTHER_PRODUCT_OPTION,
  FIRST_INSTALL_PRIMARY_PRODUCT_CHOICES,
  FIRST_INSTALL_PRODUCT_CHOICES,
  type FirstInstallProductChoice,
} from "./first-install-products";
import * as indexCss from "./index.module.css";
import { shouldPreOpenExternalTab } from "./should-pre-open-external-tab";
import { shouldRedirect } from "./should-redirect";

const tcUrl = new URL("https://www.getredo.com/terms-conditions");

const DEFAULT_HERO_IMAGE_URL =
  "https://assets.getredo.com/merchant/login-hero-20260401.webp";

const HERO_TESTIMONIALS = [
  {
    brand: "Primo Golf",
    quote:
      "The cost savings is just nuts. Having all of your products under one platform is better, and cheaper.",
  },
  {
    brand: "Popflex",
    quote:
      "Redo has transformed our returns process and saved our team countless hours.",
  },
  {
    brand: "Local Boy",
    quote:
      "Redo's software has made everything faster. It has freed up half of my day to work on other tasks.",
  },
] as const;

/**
 * Builds an onboarding-app URL with the merchant auth handoff embedded as
 * query params. Used by the user-profile menu's "Onboarding" entry and the
 * /onboarding-redirect login bounce. The post-first-install flow uses a
 * server-resolved URL so product routing stays allowlisted server-side.
 */
export function autoOnboardingUrl(token: string, teamId: string) {
  const url = new URL(REDO_ONBOARDING_APP_URL);
  url.searchParams.set("token", token);
  url.searchParams.set("teamId", teamId);
  return url;
}

export const Onboarding = memo(function Onboarding() {
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const navigate = useNavigate();
  const setupGuide = useSetupGuideOptional();
  const [step, setStep] = useState<FirstInstallStep | null>(null);
  const [attributionSource, setAttributionSource] =
    useState<FirstInstallAttributionSource | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<FirstInstallOnboardingProduct | null>(null);
  const [hoveredProduct, setHoveredProduct] =
    useState<FirstInstallOnboardingProduct | null>(null);
  const [showOtherProducts, setShowOtherProducts] = useState(false);
  const [navigationDirection, setNavigationDirection] = useState(1);
  // Handle to a tab pre-opened synchronously inside the click handler so
  // browsers (Chrome/Firefox/Safari) preserve the user-gesture activation
  // window for window.open. Setting the href after the awaited server call
  // would otherwise be popup-blocked.
  const pendingExternalTabRef = useRef<DeferredWindow | null>(null);

  const installSource = (team as { installSource?: InstallSource } | null)
    ?.installSource;
  const productSelectionRequired =
    shouldAskForFirstInstallProduct(installSource);
  const isShopifyTestStore =
    team?.name.toLowerCase().includes("shpfy test") ||
    team?.email.endsWith("@shopify.com") ||
    false;

  const onboardingLoad = useLoad(async (signal) => {
    const data = await getCurrentOnboarding(client, { signal });
    const firstStep = getInitialFirstInstallStep(data);
    if (firstStep !== "terms") {
      setStep(firstStep);
      return data;
    }

    await new Promise((r) => setTimeout(r, 1500));
    if (signal.aborted) {
      return data;
    }
    const retry = await getCurrentOnboarding(client, { signal });
    setStep(getInitialFirstInstallStep(retry));
    return retry;
  }, []);

  const [acceptTermsLoad, acceptTerms] = useTriggerLoad(async (signal) => {
    await onboardingTc(client, {
      firstInstallOnboardingVersion: FIRST_INSTALL_ONBOARDING_VERSION,
      signal,
    });
    if (signal.aborted) {
      return false;
    }
    setNavigationDirection(1);
    setStep("attribution");
    return true;
  });

  const routeToDestination = useCallback(
    async (destination: FirstInstallOnboardingDestination) => {
      const pendingTab = pendingExternalTabRef.current;
      pendingExternalTabRef.current = null;

      if (destination.type === "merchant") {
        // In-app destination — cancel the speculative tab (we only pre-open
        // it from the product-step click handler).
        pendingTab?.cancel();
        await startFirstInstallNativeSetupGuideIfAvailable(
          destination,
          setupGuide,
        );
        navigate(destination.path);
        setStep(null);
        return true;
      }

      const redirectTo = shouldRedirect({
        response: { redirectUrl: destination.redirectUrl },
        isShopifyTestStore,
      });
      if (!redirectTo) {
        pendingTab?.cancel();
        setStep(null);
        return true;
      }
      // openDeferredWindow handles the popup-blocker fallback: if the
      // speculative window was blocked, resolve() navigates the current tab
      // instead so the merchant isn't stuck on an empty modal.
      if (pendingTab) {
        pendingTab.resolve(redirectTo);
      } else {
        location.href = redirectTo;
      }
      setStep(null);
      return true;
    },
    [isShopifyTestStore, navigate, setupGuide],
  );

  const [finishLoad, finishOnboarding] = useTriggerLoad(async (signal) => {
    if (!attributionSource) {
      return false;
    }
    if (productSelectionRequired && !selectedProduct) {
      return false;
    }
    const response = await completeFirstInstallOnboarding(
      client,
      {
        attributionSource,
        selectedProduct: productSelectionRequired
          ? (selectedProduct ?? undefined)
          : undefined,
      },
      { signal },
    );
    return routeToDestination(response.destination);
  });

  const testimonial = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * HERO_TESTIMONIALS.length);
    return HERO_TESTIMONIALS[randomIndex] ?? HERO_TESTIMONIALS[0];
  }, []);
  const previewedProductChoice = useMemo(() => {
    const previewedProduct = hoveredProduct ?? selectedProduct;
    if (!previewedProduct) {
      return null;
    }
    return (
      FIRST_INSTALL_PRODUCT_CHOICES.find(
        (choice) => choice.id === previewedProduct,
      ) ?? null
    );
  }, [hoveredProduct, selectedProduct]);

  const pending = acceptTermsLoad.pending || finishLoad.pending;
  const error =
    acceptTermsLoad.error || finishLoad.error || onboardingLoad.error;

  if (!step && !error) {
    return null;
  }

  const continueDisabled =
    pending ||
    (!!error && !step) ||
    (step === "attribution" && !attributionSource) ||
    (step === "product" && !selectedProduct);
  const continueText = getFirstInstallContinueText(step, false);

  const footer = (
    <div className={indexCss.footerActions}>
      {step === "terms" && (
        <a
          className={classNames(
            indexCss.termsButton,
            indexCss.footerActionButton,
          )}
          href={tcUrl.toString()}
          rel="noreferrer"
          target="_blank"
        >
          <span>Read terms and conditions</span>
          <ArrowUpRightIcon className={indexCss.termsButtonIcon} />
        </a>
      )}

      <button
        aria-disabled={continueDisabled}
        className={classNames(
          modalCss.continueButton,
          indexCss.footerActionButton,
        )}
        data-disabled={continueDisabled ? "true" : undefined}
        onClick={
          continueDisabled
            ? undefined
            : () => {
                // Pre-open the external onboarding tab synchronously here while
                // the user-gesture activation is still valid. routeToDestination
                // runs after an awaited server call, which is past the
                // popup-blocker window in Chrome/Firefox/Safari. Only when the
                // click finishes onboarding (redirect imminent), the selected
                // product isn't the in-app chargebacks flow, and we're not on a
                // Shopify test store (those skip the redirect entirely).
                if (
                  shouldPreOpenExternalTab({
                    isShopifyTestStore,
                    productSelectionRequired,
                    selectedProduct,
                    step,
                  })
                ) {
                  pendingExternalTabRef.current = openDeferredWindow();
                }
                void handleContinue({
                  acceptTerms,
                  finishOnboarding,
                  productSelectionRequired,
                  setNavigationDirection,
                  setStep,
                  step,
                });
              }
        }
        type="button"
      >
        <span
          className={modalCss.continueText}
          data-pending={pending ? "true" : undefined}
        >
          {continueText}
        </span>
        {pending && (
          <span className={modalCss.spinnerOverlay}>
            <span className={modalCss.spinner} />
          </span>
        )}
      </button>
    </div>
  );

  return (
    <RedoElevatedOnboarding
      footer={footer}
      hideProductName
      productName="Welcome to Redo"
      rightPanel={
        <RedoElevatedOnboardingHeroPanel
          contentClassName={indexCss.heroContentEnter}
          imageUrl={DEFAULT_HERO_IMAGE_URL}
          overlay={
            <SelectedProductHeroOverlay
              product={step === "product" ? previewedProductChoice : null}
            />
          }
        >
          <div
            className={classNames(modalCss.trustedBy, indexCss.heroContentItem)}
          >
            <span className={modalCss.trustedByLabel}>
              Trusted by leading brands
            </span>
            <AnimatedLogos />
          </div>
          <blockquote
            className={classNames(
              modalCss.testimonial,
              indexCss.heroContentItem,
            )}
          >
            <p className={modalCss.quote}>{`"${testimonial.quote}"`}</p>
            <footer className={modalCss.attribution}>
              <span className={modalCss.authorName}>{testimonial.brand}</span>
            </footer>
          </blockquote>
        </RedoElevatedOnboardingHeroPanel>
      }
    >
      <div className={modalCss.stepInner}>
        <IntakeStepTransition
          direction={navigationDirection}
          enabled
          stepKey={error ? "error" : (step ?? "loading")}
        >
          <div
            className={
              step === "terms" ? modalCss.welcomeBlock : modalCss.questionsBlock
            }
          >
            <StepContent
              attributionSource={attributionSource}
              error={error}
              selectedProduct={selectedProduct}
              setAttributionSource={setAttributionSource}
              setHoveredProduct={setHoveredProduct}
              setSelectedProduct={setSelectedProduct}
              setShowOtherProducts={setShowOtherProducts}
              showOtherProducts={showOtherProducts}
              step={step}
            />
          </div>
        </IntakeStepTransition>
      </div>
    </RedoElevatedOnboarding>
  );
});

interface SelectedProductHeroOverlayProps {
  product: FirstInstallProductChoice | null;
}

const SelectedProductHeroOverlay = memo(function SelectedProductHeroOverlay({
  product,
}: SelectedProductHeroOverlayProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait">
      {product ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className={indexCss.selectedProductCardAnchor}
          exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -4 }}
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
          key={product.id}
          transition={{ duration: shouldReduceMotion ? 0 : 0.22 }}
        >
          <section
            aria-label={`${product.label} setup preview`}
            className={indexCss.selectedProductCard}
          >
            <div className={indexCss.selectedProductHeader}>
              <div className={indexCss.selectedProductTitleGroup}>
                <h2 className={indexCss.selectedProductTitle}>
                  {product.label}
                </h2>
              </div>
              <div className={indexCss.selectedProductPillGroup}>
                <span className={indexCss.selectedProductSetupPill}>
                  {product.subtext}
                </span>
              </div>
            </div>

            <p className={indexCss.selectedProductHeadline}>
              {product.hero.headline}
            </p>

            <ul className={indexCss.selectedProductBullets}>
              {product.hero.bullets.map((bullet) => (
                <li className={indexCss.selectedProductBullet} key={bullet}>
                  <span className={indexCss.selectedProductBulletDot} />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
});

const StepContent = memo(function StepContent({
  attributionSource,
  error,
  selectedProduct,
  setAttributionSource,
  setHoveredProduct,
  setSelectedProduct,
  setShowOtherProducts,
  showOtherProducts,
  step,
}: {
  attributionSource: FirstInstallAttributionSource | null;
  error: unknown;
  selectedProduct: FirstInstallOnboardingProduct | null;
  setAttributionSource: (value: FirstInstallAttributionSource) => void;
  setHoveredProduct: (value: FirstInstallOnboardingProduct | null) => void;
  setSelectedProduct: (value: FirstInstallOnboardingProduct | null) => void;
  setShowOtherProducts: (value: boolean) => void;
  showOtherProducts: boolean;
  step: FirstInstallStep | null;
}) {
  if (error) {
    return (
      <>
        <h1 className={modalCss.title}>We hit a setup issue</h1>
        <Text fontSize="md" mt="lg" textColor="tertiary">
          Could not finish onboarding. Please try again.
        </Text>
      </>
    );
  }

  if (step === "terms") {
    return (
      <>
        <h1 className={modalCss.title}>
          <DecryptedText
            animateOn="view"
            sequential
            speed={40}
            text="Welcome to Redo"
          />
        </h1>
        <p className={modalCss.subtitle}>
          Review and accept Redo's terms and conditions to get started.
        </p>
      </>
    );
  }

  if (step === "attribution") {
    return (
      <>
        <h1 className={modalCss.title}>
          {RECLAIM_ATTRIBUTION_QUESTION.prompt}
        </h1>
        <div className={modalCss.optionGrid}>
          {RECLAIM_ATTRIBUTION_QUESTION.options.map((option, index) => (
            <IntakeOption
              animateReveal
              isSelected={attributionSource === option.value}
              key={option.value}
              layout="grid"
              onClick={() => {
                const source = toFirstInstallAttributionSource(option.value);
                if (source) {
                  setAttributionSource(source);
                }
              }}
              option={option}
              revealIndex={index}
            />
          ))}
        </div>
      </>
    );
  }

  if (step === "product") {
    const renderProductOption = (
      product: FirstInstallProductChoice,
      revealIndex: number,
    ) => (
      <IntakeOption
        animateReveal
        isSelected={selectedProduct === product.id}
        key={product.id}
        layout="grid"
        onBlur={() => setHoveredProduct(null)}
        onClick={() => setSelectedProduct(product.id)}
        onFocus={() => setHoveredProduct(product.id)}
        onMouseEnter={() => setHoveredProduct(product.id)}
        onMouseLeave={() => setHoveredProduct(null)}
        option={{
          value: product.id,
          label: product.label,
          badges: product.badges,
          Icon: product.Icon,
        }}
        revealIndex={revealIndex}
      />
    );
    const showPrimaryProducts = () => {
      setHoveredProduct(null);
      setSelectedProduct(null);
      setShowOtherProducts(false);
    };
    const showOtherProductChoices = () => {
      setHoveredProduct(null);
      setSelectedProduct(null);
      setShowOtherProducts(true);
    };

    return (
      <>
        <h1 className={modalCss.title}>What should we set up first?</h1>
        <div className={modalCss.optionGrid}>
          {showOtherProducts ? (
            <>
              <IntakeOption
                animateReveal
                isSelected={false}
                key={FIRST_INSTALL_BACK_PRODUCT_OPTION.value}
                layout="grid"
                onClick={showPrimaryProducts}
                option={FIRST_INSTALL_BACK_PRODUCT_OPTION}
                revealIndex={0}
              />
              {FIRST_INSTALL_OTHER_PRODUCT_CHOICES.map((product, index) =>
                renderProductOption(product, index + 1),
              )}
            </>
          ) : (
            <>
              {FIRST_INSTALL_PRIMARY_PRODUCT_CHOICES.map((product, index) =>
                renderProductOption(product, index),
              )}
              <IntakeOption
                animateReveal
                isSelected={false}
                key={FIRST_INSTALL_OTHER_PRODUCT_OPTION.value}
                layout="grid"
                onClick={showOtherProductChoices}
                option={FIRST_INSTALL_OTHER_PRODUCT_OPTION}
                revealIndex={FIRST_INSTALL_PRIMARY_PRODUCT_CHOICES.length}
              />
            </>
          )}
        </div>
      </>
    );
  }

  return null;
});

function handleContinue(args: {
  acceptTerms: () => void;
  finishOnboarding: () => void;
  productSelectionRequired: boolean;
  setNavigationDirection: (direction: number) => void;
  setStep: (step: FirstInstallStep) => void;
  step: FirstInstallStep | null;
}): void {
  if (args.step === "terms") {
    args.acceptTerms();
    return;
  }

  const nextAction = getNextFirstInstallAction({
    step: args.step,
    productSelectionRequired: args.productSelectionRequired,
  });
  if (nextAction === "complete") {
    args.finishOnboarding();
    return;
  }
  if (nextAction) {
    args.setNavigationDirection(1);
    args.setStep(nextAction);
  }
}
